import React from 'react';
import { Avatar, AvatarGroup, Box, Tooltip, Typography } from '@mui/material';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';
import { getBackendBaseUrl } from '@/setup/apiBaseUrl';

export interface EmployeeDisplayProps {
  name?: string | null;
  nickname?: string | null;
  avatar?: string | null;
  image1?: string | null;
  employeeId?: string | null;
  userId?: string | null;
  fallbackText?: string;
  /** Chỉ hiển thị avatar, ẩn tên (mặc định true = hiện tên). */
  showName?: boolean;
}

export interface EmployeeAvatarGroupProps {
  employees: Array<{
    id?: string;
    name?: string | null;
    nickname?: string | null;
    avatar?: string | null;
    image1?: string | null;
    userId?: string | null;
  }>;
  max?: number;
}

const resolveAvatarSrc = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url) || /^data:/i.test(url)) return url;
  return `${getBackendBaseUrl()}${url}`;
};

const getDisplayName = (emp: { name?: string | null; nickname?: string | null }, fallback?: string): string =>
  String(emp.nickname || '').trim() || String(emp.name || '').trim() || fallback || '?';

export const EmployeeAvatarGroup: React.FC<EmployeeAvatarGroupProps> = ({
  employees,
  max = 4,
}) => {
  if (!employees || employees.length === 0) return null;

  const names = employees.map((e) => getDisplayName(e) || 'N/A').join(', ');

  return (
    <Tooltip title={names} arrow>
      <AvatarGroup max={max}>
        {employees.map((emp) => {
          const displayName = getDisplayName(emp);
          const src = resolveAvatarSrc(emp.image1 || emp.avatar);
          return (
            <Avatar
              key={emp.id}
              src={src}
              alt={displayName}
              data-employee-id={emp.id || undefined}
              data-user-id={emp.userId || undefined}
              sx={{
                width: 22,
                height: 22,
                fontSize: '0.65rem',
                backgroundColor: '#3b82f6',
                fontWeight: 600,
              }}
            >
              {!src ? displayName.charAt(0).toUpperCase() : undefined}
            </Avatar>
          );
        })}
      </AvatarGroup>
    </Tooltip>
  );
};

const EmployeeDisplay: React.FC<EmployeeDisplayProps> = ({
  name,
  nickname,
  avatar,
  image1,
  employeeId,
  userId,
  fallbackText = 'N/A',
  showName = true,
}) => {
  const displayName = getDisplayName({ name, nickname }, fallbackText);
  const avatarChar = displayName !== fallbackText ? displayName.charAt(0).toUpperCase() : '?';
  const avatarSrc = resolveAvatarSrc(image1 || avatar);

  return (
    <Box
      sx={sharedListStyles.avatarCell}
      data-employee-id={employeeId || undefined}
      data-user-id={userId || undefined}
    >
      <Avatar
        src={avatarSrc}
        sx={{ ...sharedListStyles.avatar, width: 20, height: 20, fontSize: '0.7rem' }}
      >
        {avatarChar}
      </Avatar>
      {showName ? (
        <Box>
          <Typography variant="body2" sx={sharedListStyles.cellText}>
            {displayName}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

interface UserOptionData {
  id?: string;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  nickname?: string | null;
  fullName?: string | null;
  full_name?: string | null;
  avatar?: string | null;
  image1?: string | null;
  email?: string | null;
  username?: string | null;
}

export interface UserOptionDisplayProps {
  user: UserOptionData;
}

export const UserOptionDisplay: React.FC<UserOptionDisplayProps> = ({ user }) => {
  const firstName = String(user.first_name || '').trim();
  const lastName = String(user.last_name || '').trim();
  const combinedName = `${firstName} ${lastName}`.trim();
  const fullName =
    String(user.nickname || '').trim()
    || combinedName
    || String(user.name || user.fullName || user.full_name || '').trim()
    || String(user.username || user.email || '').trim()
    || 'N/A';
  const avatarChar = fullName !== 'N/A' ? fullName.charAt(0).toUpperCase() : '?';
  const avatarSrc = resolveAvatarSrc((user as any).image1 || user.avatar);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar
        src={avatarSrc}
        sx={{ width: 24, height: 24, fontSize: '0.75rem', backgroundColor: '#3b82f6', fontWeight: 600 }}
      >
        {!avatarSrc ? avatarChar : undefined}
      </Avatar>
      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#111827' }}>
        {fullName}
      </Typography>
    </Box>
  );
};

export default EmployeeDisplay;
